import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';
import axios from '../services/api';
import { AuthContext } from '../auth/AuthContext';
import {
  MessageAttachmentList,
  MessageAttachmentPicker,
  downloadMessageAttachment,
} from '../components/MessageAttachments';

const isBookingParent = (contact) => contact?.kind === 'booking_parent';
const contactKey = (contact) => `${contact?.kind || 'lms_user'}:${contact?._id || ''}`;

const getInitials = (name) => {
  if (!name) return '';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || '';
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const normalizeLmsContact = (contact) => ({ ...contact, kind: 'lms_user' });
const normalizeBookingParent = (contact) => ({ ...contact, kind: 'booking_parent' });

const ChatMessage = ({ message, isMine, activeChatUser, currentUser, onDownload }) => {
  const name = isMine ? currentUser?.name : activeChatUser?.name;
  return (
    <Stack direction={isMine ? 'row-reverse' : 'row'} spacing={1.5} alignItems="flex-end">
      <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>{getInitials(name)}</Avatar>
      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1.25,
          borderRadius: 18,
          maxWidth: '70%',
          bgcolor: isMine ? 'primary.light' : 'background.paper',
        }}
      >
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{message.content}</Typography>
        <MessageAttachmentList attachments={message.attachments} onDownload={(attachment) => onDownload(message, attachment)} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
          {new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </Typography>
      </Paper>
    </Stack>
  );
};

const AdminMessaging = () => {
  const { token, user } = useContext(AuthContext) ?? {};
  const [searchParams] = useSearchParams();
  const requestedContactId = String(searchParams.get('contactId') || '');
  const requestedBookingNumber = String(searchParams.get('bookingNumber') || '');
  const requestedSubject = String(searchParams.get('subject') || '');

  const [tutors, setTutors] = useState([]);
  const [students, setStudents] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [supportContacts, setSupportContacts] = useState([]);
  const [isOversight, setIsOversight] = useState(false);
  const [replyAsSupportContactId, setReplyAsSupportContactId] = useState('');
  const [bookingParents, setBookingParents] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [sending, setSending] = useState(false);

  const [unreadMap, setUnreadMap] = useState({});
  const [bookingUnreadMap, setBookingUnreadMap] = useState({});
  const [activeChatUser, setActiveChatUser] = useState(null);
  const [messageContent, setMessageContent] = useState('');
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [search, setSearch] = useState('');
  const [sortMode, setSortMode] = useState('lastMessaged');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const messagesEndRef = useRef(null);
  const sseRef = useRef(null);
  const activeChatRef = useRef(null);
  const contextPrefillRef = useRef('');

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    activeChatRef.current = activeChatUser;
  }, [activeChatUser]);

  useEffect(() => setAttachmentFiles([]), [activeChatUser?._id]);

  const fetchUnreadCounts = useCallback(async () => {
    if (!token) return;
    try {
      const [lmsUnread, bookingUnread] = await Promise.all([
        axios.get('/messages/unread-counts', { headers: authHeaders }).catch(() => ({ data: {} })),
        axios.get('/booking-v1/lms-messages/unread-counts', { headers: authHeaders }).catch(() => ({ data: {} })),
      ]);
      setUnreadMap(lmsUnread?.data || {});
      setBookingUnreadMap(bookingUnread?.data || {});
    } catch (err) {
      console.warn('Failed to fetch unread counts', err);
    }
  }, [token, authHeaders]);

  const markBatchRead = useCallback(async (list, contact = activeChatUser) => {
    if (!token || !user?._id || !contact) return;
    const myId = String(user._id);
    const unread = (list || []).filter((m) => {
      if (isBookingParent(contact)) {
        const readBy = (m?.readByLmsUsers || []).map((id) => String(id?._id || id));
        return m?.senderType === 'parent' && !readBy.includes(myId);
      }
      const senderId = String(m?.sender?._id || m?.sender || '');
      const readBy = (m?.readBy || []).map(String);
      return senderId !== myId && !readBy.includes(myId);
    });
    const ids = unread.map((m) => m._id).filter(Boolean);
    if (!ids.length) return;
    try {
      await axios.post(
        isBookingParent(contact) ? '/booking-v1/lms-messages/read-batch' : '/messages/read-batch',
        { ids },
        { headers: authHeaders },
      );
      if (isBookingParent(contact)) {
        setBookingUnreadMap((prev) => ({ ...(prev || {}), [contact._id]: 0 }));
      } else {
        setUnreadMap((prev) => ({ ...(prev || {}), [contact._id]: 0 }));
      }
    } catch (err) {
      console.warn('Failed to mark messages as read', err);
    }
  }, [token, user?._id, authHeaders, activeChatUser]);

  const fetchMessages = useCallback(async (contact, opts = {}) => {
    const { pageToLoad = 1, appendOlder = false } = opts;
    if (!contact?._id || !token) {
      setMessages([]);
      return;
    }
    if (appendOlder) setLoadingOlder(true);
    else setLoadingMessages(true);
    try {
      const endpoint = isBookingParent(contact) ? '/booking-v1/lms-messages' : '/messages';
      const params = isBookingParent(contact)
        ? { withParent: contact._id, page: pageToLoad, limit: 50 }
        : { withUser: contact._id, page: pageToLoad, limit: 50 };
      const res = await axios.get(endpoint, { headers: authHeaders, params });
      const payload = res?.data ?? {};
      const list = Array.isArray(payload.items) ? payload.items : [];
      const ascending = [...list].reverse();
      setHasMore(Boolean(payload.hasMore));
      setPage(pageToLoad);
      setMessages((prev) => (appendOlder ? [...ascending, ...prev] : ascending));
      await markBatchRead(list, contact);
    } catch (err) {
      toast.error('Failed to load conversation');
      if (!appendOlder) setMessages([]);
    } finally {
      setLoadingMessages(false);
      setLoadingOlder(false);
    }
  }, [token, authHeaders, markBatchRead]);

  const loadUsers = useCallback(async () => {
    if (!token || !user?._id) return;
    setLoadingUsers(true);
    try {
      const resAll = await axios.get('/messages/admin/contacts', { headers: authHeaders, params: { sort: sortMode } });
      const groups = resAll?.data?.groups || {};
      const tutorsList = (Array.isArray(groups.tutors) ? groups.tutors : []).map(normalizeLmsContact);
      const studentsList = (Array.isArray(groups.students) ? groups.students : []).map(normalizeLmsContact);
      const adminListFiltered = (Array.isArray(groups.admins) ? groups.admins : []).map(normalizeLmsContact);
      const supportList = Array.isArray(resAll?.data?.supportContacts) ? resAll.data.supportContacts : [];
      const bookingParentList = (Array.isArray(groups.parents) ? groups.parents : []).map(normalizeBookingParent);
      setUnreadMap(Object.fromEntries([...tutorsList, ...studentsList, ...adminListFiltered].map((contact) => [contact._id, Number(contact.unreadCount || 0)])));
      setBookingUnreadMap(Object.fromEntries(bookingParentList.map((contact) => [contact._id, Number(contact.unreadCount || 0)])));

      setTutors(tutorsList);
      setStudents(studentsList);
      setAdmins(adminListFiltered);
      setSupportContacts(supportList);
      setIsOversight(Boolean(resAll?.data?.oversight));
      setReplyAsSupportContactId((current) => current && supportList.some((item) => String(item._id) === String(current)) ? current : String(supportList[0]?._id || ''));
      setBookingParents(bookingParentList);
      const nextPool = [...adminListFiltered, ...tutorsList, ...bookingParentList, ...studentsList];
      const requested = requestedContactId ? nextPool.find((contact) => String(contact._id) === requestedContactId) : null;
      setActiveChatUser((prev) => requested || (prev && nextPool.some((c) => contactKey(c) === contactKey(prev)) ? prev : null));
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  }, [token, user?._id, authHeaders, sortMode, requestedContactId]);

  useEffect(() => {
    if (!activeChatUser || String(activeChatUser._id) !== requestedContactId) return;
    const contextKey = `${requestedContactId}:${requestedBookingNumber}:${requestedSubject}`;
    if (!requestedBookingNumber || contextPrefillRef.current === contextKey) return;
    contextPrefillRef.current = contextKey;
    setMessageContent(`Regarding booking ${requestedBookingNumber}${requestedSubject ? ` (${requestedSubject})` : ''}: `);
  }, [activeChatUser, requestedContactId, requestedBookingNumber, requestedSubject]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    fetchUnreadCounts();
    const iv = setInterval(fetchUnreadCounts, 30000);
    const onNew = (event) => {
      const detail = event.detail || {};
      const from = detail.from;
      const active = activeChatRef.current;
      if (!from || (active && !isBookingParent(active) && active._id === from)) return;
      setUnreadMap((prev) => ({ ...(prev || {}), [from]: Number(prev?.[from] || 0) + 1 }));
    };
    window.addEventListener('lms:new-message', onNew);
    return () => {
      clearInterval(iv);
      window.removeEventListener('lms:new-message', onNew);
    };
  }, [fetchUnreadCounts]);

  useEffect(() => {
    if (activeChatUser) fetchMessages(activeChatUser, { pageToLoad: 1, appendOlder: false });
    else setMessages([]);
  }, [activeChatUser, fetchMessages]);

  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!token || !activeChatUser?._id || !isBookingParent(activeChatUser)) return undefined;
    const interval = setInterval(() => {
      fetchMessages(activeChatUser, { pageToLoad: 1, appendOlder: false });
      fetchUnreadCounts();
    }, 20000);
    return () => clearInterval(interval);
  }, [token, activeChatUser, fetchMessages, fetchUnreadCounts]);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;

    const connectStream = async () => {
      try {
        const { data } = await axios.post('/messages/stream-token', {}, { headers: authHeaders });
        if (cancelled) return;
        const streamToken = data?.token;
        if (!streamToken) return;
        const base = process.env.REACT_APP_API_BASE_URL || axios?.defaults?.baseURL || '';
        const url = `${base.replace(/\/$/, '')}/messages/stream?token=${encodeURIComponent(streamToken)}`;
        const es = new EventSource(url);
        sseRef.current = es;

        es.addEventListener('message', (ev) => {
          let payload;
          try {
            payload = JSON.parse(ev.data || '{}');
          } catch {
            return;
          }
          if (payload?.type !== 'new-message') return;
          const active = activeChatRef.current;
          if (!active || isBookingParent(active)) return;
          const myId = String(user?._id || '');
          const from = String(payload.from || '');
          const toRaw = payload.to ?? payload.recipients ?? [];
          const toIds = Array.isArray(toRaw) ? toRaw.map(String) : [String(toRaw)];
          const activeId = String(active?._id || '');
          const involved = from === myId || toIds.includes(myId);
          if (!involved || !activeId) return;
          const isActive = from === activeId || toIds.includes(activeId);
          if (!isActive) return;

          const messageId = payload.id || payload._id;
          setMessages((prev) => {
            if (messageId && prev.some((m) => String(m._id) === String(messageId))) return prev;
            const senderIsMe = from === myId;
            return [...prev, {
              _id: messageId || `sse-${Date.now()}`,
              sender: { _id: senderIsMe ? myId : activeId, name: senderIsMe ? user?.name : payload.fromName || active?.name },
              recipients: toIds,
              content: payload.content,
              attachments: payload.attachments || [],
              createdAt: payload.at || new Date().toISOString(),
            }];
          });

          if (from !== myId && payload.id) {
            axios.post('/messages/read-batch', { ids: [payload.id] }, { headers: authHeaders }).catch(() => {});
          }
        });
      } catch (err) {
        console.warn('Failed to connect message stream', err);
      }
    };

    connectStream();
    return () => {
      cancelled = true;
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
    };
  }, [token, user?._id, user?.name, authHeaders]);

  const filteredList = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matches = (userList) => (!term ? userList : userList.filter((u) => `${u.name} ${u.email} ${u.summary || ''}`.toLowerCase().includes(term)));
    return {
      bookingParents: matches(bookingParents),
      tutors: matches(tutors),
      students: matches(students),
      admins: matches(admins),
    };
  }, [search, bookingParents, tutors, students, admins]);

  const openConversation = (chatUser) => {
    setActiveChatUser(chatUser);
    if (isBookingParent(chatUser)) {
      setBookingUnreadMap((prev) => {
        const copy = { ...(prev || {}) };
        delete copy[chatUser._id];
        return copy;
      });
    } else {
      setUnreadMap((prev) => {
        const copy = { ...(prev || {}) };
        delete copy[chatUser._id];
        return copy;
      });
    }
  };

  const handleSendMessage = async () => {
    if (!activeChatUser) {
      toast.info('Select a user before sending a message.');
      return;
    }
    if (!messageContent.trim() && !attachmentFiles.length) {
      toast.info('Enter a message or attach a document.');
      return;
    }
    setSending(true);
    try {
      const endpoint = isBookingParent(activeChatUser) ? '/booking-v1/lms-messages/send' : '/messages/send';
      const validReplyAs = replyAsChoices.some((contact) => String(contact._id) === String(replyAsSupportContactId))
        ? replyAsSupportContactId
        : replyAsChoices[0]?._id;
      const delegated = isOversight && validReplyAs ? { asSupportContactId: validReplyAs } : {};
      const body = new FormData();
      if (isBookingParent(activeChatUser)) body.append('parentId', activeChatUser._id);
      else body.append('recipients', activeChatUser._id);
      body.append('content', messageContent.trim());
      Object.entries(delegated).forEach(([key, value]) => body.append(key, value));
      attachmentFiles.forEach((file) => body.append('attachments', file));
      await axios.post(endpoint, body, { headers: authHeaders, timeout: 45000 });
      setMessageContent('');
      setAttachmentFiles([]);
      await fetchMessages(activeChatUser, { pageToLoad: 1, appendOlder: false });
      fetchUnreadCounts();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleDownloadAttachment = async (message, attachment) => {
    const endpoint = isBookingParent(activeChatUser)
      ? `/booking-v1/lms-messages/${message._id}/attachments/${attachment.id}`
      : `/messages/${message._id}/attachments/${attachment.id}`;
    try {
      await downloadMessageAttachment(axios, endpoint, attachment.name);
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.response?.data?.error || 'The attachment could not be downloaded.');
    }
  };

  const formatLatestMessageTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const contactSubtitle = (item) => {
    const base = isBookingParent(item)
      ? item.summary || item.email
      : item.messagingLabel || item.subtitle || item.meta?.messagingLabel || item.email;
    const latestTime = formatLatestMessageTime(item.latestMessageAt);
    const latestPreview = String(item.latestMessagePreview || '').trim();
    if (latestTime || latestPreview) {
      return [latestTime, latestPreview || base].filter(Boolean).join(' - ');
    }
    return base || 'No messages yet';
  };

  const replyAsChoices = useMemo(() => {
    if (!activeChatUser) return supportContacts;
    const audience = isBookingParent(activeChatUser) ? 'booking_parent' : String(activeChatUser.role || '').toLowerCase();
    return supportContacts.filter((contact) => (contact.messagingAudiences || []).includes(audience));
  }, [activeChatUser, supportContacts]);

  const renderUserGroup = (title, list) => (
    <Box key={title}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', px: 1.5, pb: 0.5 }}>
        {title}
      </Typography>
      <List dense disablePadding>
        {list.length === 0 ? (
          <ListItem><ListItemText primary="No users" primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }} /></ListItem>
        ) : (
          list.map((item) => {
            const unread = Number(isBookingParent(item) ? bookingUnreadMap?.[item._id] || 0 : unreadMap?.[item._id] || 0);
            const isActive = contactKey(activeChatUser) === contactKey(item);
            return (
              <ListItemButton key={contactKey(item)} selected={isActive} onClick={() => openConversation(item)}>
                <Badge color="error" badgeContent={unread} overlap="circular" invisible={unread === 0}>
                  <Avatar sx={{ mr: 1.5, bgcolor: 'primary.main', color: 'primary.contrastText' }}>{getInitials(item.name || item.email)}</Avatar>
                </Badge>
                <ListItemText
                  primary={item.name || item.email}
                  secondary={contactSubtitle(item)}
                  primaryTypographyProps={{ fontWeight: isActive ? 700 : 500 }}
                />
              </ListItemButton>
            );
          })
        )}
      </List>
    </Box>
  );

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <Card>
          <CardHeader
            action={<IconButton onClick={loadUsers} disabled={loadingUsers}>{loadingUsers ? <CircularProgress size={18} /> : <RefreshRoundedIcon />}</IconButton>}
          />
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                placeholder="Search users"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
              />
              <TextField
                select
                label="Sort"
                value={sortMode}
                onChange={(event) => setSortMode(event.target.value)}
                sx={{ minWidth: { xs: '100%', md: 190 } }}
              >
                <MenuItem value="lastMessaged">Last messaged</MenuItem>
                <MenuItem value="alpha">Alphabetical</MenuItem>
              </TextField>
            </Stack>
          </CardContent>
        </Card>

        <Stack className={activeChatUser ? 'admin-messaging-chat-active' : ''} direction={{ xs: 'column', lg: 'row' }} spacing={3} sx={{ height: { lg: 'calc(100vh - 260px)', xs: 'auto' }, minHeight: { lg: 520, xs: 0 } }}>
          <Card sx={{ width: { lg: 320, xs: '100%' }, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {loadingUsers && <LinearProgress />}
            <CardContent sx={{ flex: 1, minHeight: 0, overflow: 'auto', py: 2 }}>
              <Stack spacing={2}>
                {filteredList.admins.length ? renderUserGroup('Admins', filteredList.admins) : null}
                {renderUserGroup('Tutors', filteredList.tutors)}
                {renderUserGroup('Parents', filteredList.bookingParents)}
                {renderUserGroup('Students', filteredList.students)}
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <CardHeader
              title={activeChatUser ? activeChatUser.name || activeChatUser.email : 'Select a user to start chatting'}
              subheader={activeChatUser ? (isBookingParent(activeChatUser) ? activeChatUser.summary || activeChatUser.email : activeChatUser.email) : 'Messages appear here once a user is selected.'}
              action={activeChatUser ? <Button className="message-mobile-back" variant="outlined" size="small" onClick={() => setActiveChatUser(null)}>All messages</Button> : null}
            />
            <Divider />
            <CardContent sx={{ flex: 1, minHeight: 0, overflowY: 'auto', bgcolor: 'grey.50' }}>
              {loadingMessages ? (
                <Stack height="100%" alignItems="center" justifyContent="center" spacing={1}><CircularProgress size={32} /><Typography variant="body2" color="text.secondary">Loading conversation...</Typography></Stack>
              ) : !activeChatUser ? (
                <Stack height="100%" alignItems="center" justifyContent="center" spacing={1}><Typography variant="body2" color="text.secondary">Choose a contact from the sidebar to view messages.</Typography></Stack>
              ) : messages.length === 0 ? (
                <Stack height="100%" alignItems="center" justifyContent="center" spacing={1}><Typography variant="body2" color="text.secondary">No messages yet. Start the conversation below.</Typography></Stack>
              ) : (
                <Stack spacing={1.5}>
                  {hasMore && (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Button size="small" variant="outlined" onClick={() => fetchMessages(activeChatUser, { pageToLoad: page + 1, appendOlder: true })} disabled={loadingOlder}>{loadingOlder ? 'Loading...' : 'Load older messages'}</Button>
                    </Box>
                  )}
                  {messages.map((msg) => {
                    const mine = isBookingParent(activeChatUser)
                      ? msg.senderType === 'lms_user' || String(msg.senderUserId?._id || msg.senderUserId || '') === String(user?._id || '')
                      : String(msg.sender?._id || msg.sender || '') === String(user?._id || '');
                    return <ChatMessage key={msg._id} message={msg} isMine={mine} activeChatUser={activeChatUser} currentUser={user} onDownload={handleDownloadAttachment} />;
                  })}
                  <Box ref={messagesEndRef} />
                </Stack>
              )}
            </CardContent>
            <Divider />
            <CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="flex-end">
                {isOversight ? (
                  <TextField select label="Reply as" value={replyAsChoices.some((contact) => String(contact._id) === String(replyAsSupportContactId)) ? replyAsSupportContactId : String(replyAsChoices[0]?._id || '')} onChange={(event) => setReplyAsSupportContactId(event.target.value)} disabled={!activeChatUser || sending} sx={{ minWidth: { xs: '100%', md: 190 } }}>
                    {replyAsChoices.map((contact) => <MenuItem key={contact._id} value={contact._id}>{contact.name} - {contact.messagingLabel}</MenuItem>)}
                  </TextField>
                ) : null}
                <MessageAttachmentPicker files={attachmentFiles} onChange={setAttachmentFiles} disabled={!activeChatUser || sending} onError={toast.error} />
                <TextField
                  label={activeChatUser ? `Message ${activeChatUser.name || activeChatUser.email}` : 'Select a user to enable messaging'}
                  placeholder="Type your message"
                  multiline
                  minRows={2}
                  value={messageContent}
                  onChange={(event) => setMessageContent(event.target.value)}
                  disabled={!activeChatUser || sending}
                  fullWidth
                />
                <Button variant="contained" endIcon={sending ? <CircularProgress size={18} color="inherit" /> : <SendRoundedIcon />} onClick={handleSendMessage} disabled={!activeChatUser || sending || (!messageContent.trim() && !attachmentFiles.length)}>
                  Send
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Stack>
    </DashboardLayout>
  );
};

export default AdminMessaging;
